/**
 * Attribution tracking utilities
 * Captures and processes attribution data (UTM parameters, referrer, etc.)
 */

export interface AttributionData {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  referrer?: string;
  landingPage?: string;
  source?: string;
  medium?: string;
}

const ATTRIBUTION_COOKIE_NAME = "rim_attribution";
const ATTRIBUTION_COOKIE_MAX_AGE = 30 * 24 * 60 * 60; // 30 days in seconds

/**
 * Extract UTM parameters and other attribution data from URL
 */
export function captureAttribution(): AttributionData | null {
  try {
    if (typeof window === "undefined") {
      return null;
    }

    const url = new URL(window.location.href);
    const searchParams = url.searchParams;

    const attribution: AttributionData = {
      utmSource: searchParams.get("utm_source") || undefined,
      utmMedium: searchParams.get("utm_medium") || undefined,
      utmCampaign: searchParams.get("utm_campaign") || undefined,
      utmTerm: searchParams.get("utm_term") || undefined,
      utmContent: searchParams.get("utm_content") || undefined,
      referrer: document.referrer || undefined,
      landingPage: window.location.pathname + window.location.search,
    };

    // Google Ads params on landing URL (gclid, gad_source, gbraid) count as attribution
    const landingUrl = url.pathname + url.search;
    const hasGoogleAdsParams =
      landingUrl.includes("gclid=") ||
      landingUrl.includes("gad_source=") ||
      landingUrl.includes("gbraid=");

    // Only return if we have at least some attribution data
    if (
      attribution.utmSource ||
      attribution.utmMedium ||
      attribution.utmCampaign ||
      attribution.referrer ||
      hasGoogleAdsParams
    ) {
      // Detect source and medium
      const { source, medium } = detectSourceMedium(attribution);
      attribution.source = source;
      attribution.medium = medium;

      return attribution;
    }

    return null;
  } catch (error) {
    // Silent failure - log but don't throw
    console.error("Failed to capture attribution:", error);
    return null;
  }
}

/**
 * Detect source and medium from attribution data
 * Prioritizes UTM parameters, falls back to referrer analysis
 */
export function detectSourceMedium(
  attribution: AttributionData
): { source: string; medium: string } {
  try {
    // If UTM parameters are present, use them
    if (attribution.utmSource && attribution.utmMedium) {
      return {
        source: attribution.utmSource.toLowerCase(),
        medium: attribution.utmMedium.toLowerCase(),
      };
    }

    // If we have UTM source but no medium, infer medium
    if (attribution.utmSource) {
      const source = attribution.utmSource.toLowerCase();
      const medium = attribution.utmMedium?.toLowerCase() || "unknown";
      return { source, medium };
    }

    // No referrer: check if landing URL has Google Ads params (e.g. referrer stripped)
    if (!attribution.referrer) {
      const landingHasGoogleAds =
        attribution.landingPage &&
        (attribution.landingPage.includes("gclid=") ||
          attribution.landingPage.includes("gad_source=") ||
          attribution.landingPage.includes("gbraid="));
      if (landingHasGoogleAds) {
        return { source: "google", medium: "cpc" };
      }
      return { source: "(direct)", medium: "(none)" };
    }

    try {
      const referrerUrl = new URL(attribution.referrer);
      const hostname = referrerUrl.hostname.toLowerCase();

      // Google
      if (hostname.includes("google")) {
        // Paid: gclid/gad_source/gbraid live on the landing URL, not the referrer.
        // Referrer from a Google Ad click is typically just https://www.google.com/
        const referrerHasPaid = attribution.referrer.includes("gclid=");
        const landingHasPaid =
          (attribution.landingPage &&
            (attribution.landingPage.includes("gclid=") ||
              attribution.landingPage.includes("gad_source=") ||
              attribution.landingPage.includes("gbraid="))) ||
          false;
        const isPaid = referrerHasPaid || landingHasPaid;
        return {
          source: "google",
          medium: isPaid ? "cpc" : "organic",
        };
      }

      // Facebook/Instagram
      if (
        hostname.includes("facebook.com") ||
        hostname.includes("instagram.com") ||
        hostname.includes("fb.com")
      ) {
        return { source: "facebook", medium: "social" };
      }

      // Twitter/X
      if (hostname.includes("twitter.com") || hostname.includes("x.com")) {
        return { source: "twitter", medium: "social" };
      }

      // LinkedIn
      if (hostname.includes("linkedin.com")) {
        return { source: "linkedin", medium: "social" };
      }

      // YouTube
      if (hostname.includes("youtube.com") || hostname.includes("youtu.be")) {
        return { source: "youtube", medium: "social" };
      }

      // Reddit
      if (hostname.includes("reddit.com")) {
        return { source: "reddit", medium: "social" };
      }

      // Other search engines
      if (
        hostname.includes("bing.com") ||
        hostname.includes("duckduckgo.com") ||
        hostname.includes("yahoo.com")
      ) {
        return { source: hostname.split(".")[0], medium: "organic" };
      }

      // Generic referral
      return {
        source: hostname.replace("www.", ""),
        medium: "referral",
      };
    } catch {
      // Invalid referrer URL, treat as direct
      return { source: "(direct)", medium: "(none)" };
    }
  } catch (error) {
    // Silent failure - return defaults
    console.error("Failed to detect source/medium:", error);
    return { source: "(direct)", medium: "(none)" };
  }
}

/**
 * Set attribution cookie on client side
 */
export function setAttributionCookie(attribution: AttributionData): boolean {
  try {
    if (typeof document === "undefined") {
      return false;
    }

    // Check if cookie already exists - only set on first visit
    if (getAttributionCookie()) {
      return false;
    }

    const cookieValue = JSON.stringify(attribution);
    const expires = new Date();
    expires.setTime(expires.getTime() + ATTRIBUTION_COOKIE_MAX_AGE * 1000);

    document.cookie = `${ATTRIBUTION_COOKIE_NAME}=${encodeURIComponent(
      cookieValue
    )}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;

    return true;
  } catch (error) {
    // Silent failure - log but don't throw
    console.error("Failed to set attribution cookie:", error);
    return false;
  }
}

/**
 * Get attribution cookie on client side
 */
export function getAttributionCookie(): AttributionData | null {
  try {
    if (typeof document === "undefined") {
      return null;
    }

    const cookies = document.cookie.split(";");
    const attributionCookie = cookies.find((cookie) =>
      cookie.trim().startsWith(`${ATTRIBUTION_COOKIE_NAME}=`)
    );

    if (!attributionCookie) {
      return null;
    }

    const value = attributionCookie.split("=").slice(1).join("=");
    const decoded = decodeURIComponent(value);
    return JSON.parse(decoded) as AttributionData;
  } catch (error) {
    // Silent failure - invalid cookie
    console.error("Failed to get attribution cookie:", error);
    return null;
  }
}

/**
 * Clear attribution cookie
 */
export function clearAttributionCookie(): boolean {
  try {
    if (typeof document === "undefined") {
      return false;
    }

    document.cookie = `${ATTRIBUTION_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;

    return true;
  } catch (error) {
    // Silent failure
    console.error("Failed to clear attribution cookie:", error);
    return false;
  }
}

/**
 * Get attribution from request (server-side)
 */
export function getAttributionFromRequest(
  request: Request
): AttributionData | null {
  try {
    const cookieHeader = request.headers.get("cookie");
    if (!cookieHeader) {
      return null;
    }

    const cookies = cookieHeader.split(";").map((c) => c.trim());
    const attributionCookie = cookies.find((cookie) =>
      cookie.startsWith(`${ATTRIBUTION_COOKIE_NAME}=`)
    );

    if (!attributionCookie) {
      return null;
    }

    const value = attributionCookie.split("=").slice(1).join("=");
    const decoded = decodeURIComponent(value);
    return JSON.parse(decoded) as AttributionData;
  } catch (error) {
    // Silent failure - invalid cookie
    console.error("Failed to get attribution from request:", error);
    return null;
  }
}

/**
 * Clear attribution cookie from response (server-side)
 */
export function clearAttributionCookieHeader(): string {
  return `${ATTRIBUTION_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

/**
 * Get attribution from Next.js cookies() (server-side)
 * Use this in server components, route handlers, or database hooks
 * Note: In Next.js 15+, cookies() returns a Promise, so await it first
 */
export function getAttributionFromNextCookies(
  cookieStore: Awaited<ReturnType<typeof import("next/headers").cookies>>
): AttributionData | null {
  try {
    const attributionCookie = cookieStore.get(ATTRIBUTION_COOKIE_NAME);
    
    if (!attributionCookie?.value) {
      return null;
    }

    const decoded = decodeURIComponent(attributionCookie.value);
    return JSON.parse(decoded) as AttributionData;
  } catch (error) {
    // Silent failure - invalid cookie
    console.error("Failed to get attribution from Next.js cookies:", error);
    return null;
  }
}
